<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-4xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Renew Crediting Period</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">Renew Crediting Period</h1>
            <p class="text-gray-600">
              Article 6.4 crediting periods are typically 5 years and may be renewed up to 3 times (15 years total for most project types). Apply for renewal before your current crediting period expires.
            </p>
          </div>
        </section>

        <!-- Current status -->
        <div class="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 class="text-lg font-semibold text-accent mb-4">Current Crediting Period</h2>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p class="text-gray-500">Project</p><p class="font-medium text-gray-800">Solar Park Beta</p></div>
            <div><p class="text-gray-500">Period Number</p><p class="font-medium text-gray-800">1 of 3</p></div>
            <div><p class="text-gray-500">Start Date</p><p class="font-medium text-gray-800">01 Apr 2024</p></div>
            <div><p class="text-gray-500">End Date</p><p class="font-medium text-primary font-semibold">31 Mar 2029</p></div>
          </div>
          <div class="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            <i class="fas fa-clock mr-2"></i>Renewal application should be submitted at least 6 months before expiry (by <strong>30 Sep 2028</strong>).
          </div>
        </div>

        <form class="space-y-6">
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-redo text-primary mr-3"></i>Renewal Application
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Select Project *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Solar Park Beta – Northern Highlands (expires 31 Mar 2029)</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Proposed New Crediting Period End Date *</label>
                <input type="date" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Justification for Renewal *</label>
                <textarea rows="4" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Explain why the project should be renewed, any changes in project scope, continued additionality, and updated baseline assessment."></textarea>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-4 flex items-center">
              <i class="fas fa-upload text-primary mr-3"></i>Required Documents
            </h2>
            <div class="space-y-4">
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="font-medium text-accent">Updated Baseline Assessment *</h3>
                  <span class="text-xs text-error">Required</span>
                </div>
                <div class="border-2 border-dashed border-gray-300 rounded-lg p-5 text-center hover:border-primary transition-colors cursor-pointer">
                  <i class="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-1"></i>
                  <p class="text-sm text-gray-600">Drag and drop or click to upload</p>
                  <p class="text-xs text-gray-500">PDF up to 10MB</p>
                </div>
              </div>
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="font-medium text-accent">Updated Additionality Demonstration *</h3>
                  <span class="text-xs text-error">Required</span>
                </div>
                <div class="border-2 border-dashed border-gray-300 rounded-lg p-5 text-center hover:border-primary transition-colors cursor-pointer">
                  <i class="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-1"></i>
                  <p class="text-sm text-gray-600">Drag and drop or click to upload</p>
                  <p class="text-xs text-gray-500">PDF up to 10MB</p>
                </div>
              </div>
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="font-medium text-accent">Updated Host Country Authorization (LoA)</h3>
                  <span class="text-xs text-gray-400">If changed</span>
                </div>
                <div class="border-2 border-dashed border-gray-300 rounded-lg p-5 text-center hover:border-primary transition-colors cursor-pointer">
                  <i class="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-1"></i>
                  <p class="text-sm text-gray-600">Drag and drop or click to upload</p>
                  <p class="text-xs text-gray-500">PDF up to 10MB</p>
                </div>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <label class="flex items-start space-x-3 cursor-pointer">
              <input type="checkbox" class="mt-1 rounded border-gray-300 text-primary focus:ring-primary" />
              <span class="text-sm text-gray-700">I confirm the information provided is accurate. I understand the renewal is subject to DOE validation and government approval.</span>
            </label>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <button type="button" class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                <i class="fas fa-save mr-2"></i>Save Draft
              </button>
              <div class="flex items-center space-x-3">
                <div class="text-right text-sm text-gray-600">
                  <p>Renewal Fee <span class="font-semibold text-primary">$300.00</span></p>
                </div>
                <button type="submit" class="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90">
                  <i class="fas fa-paper-plane mr-2"></i>Submit Renewal
                </button>
              </div>
            </div>
          </section>
        </form>

      </div>
    </main>
  </div>
</template>
